import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  Paper,
  Collapse,
  Pagination,
} from "@mui/material";
import {
  Search,
  Close,
  CheckBoxOutlineBlank,
  CheckBox,
  CloudUpload,
  InfoOutlined,
  ExpandMore,
} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useDebounce } from "../hooks/useDebounce";
import useApiEp from "../hooks/useApiEp";
import { getErrorResponse, getToken } from "../utils/util";
import { API_ENDPOINT } from "../utils/constants";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// --- Cropper Imports ---
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

const allowedHeights = {
  SHORT: 112,
  MEDIUM: 168,
  TALL: 264,
};
const aspectRatios = [
  { label: "2:1", value: 2 / 1 },
  { label: "16:9", value: 16 / 9 },
  { label: "7:3", value: 7 / 3 },
];

const MediaSelectionModal = ({ dialog, onClose, onMediaSelected }) => {
  const [media, setMedia] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [allMediaMap, setAllMediaMap] = useState(new Map());
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(aspectRatios[0].value);
  const [targetHeight, setTargetHeight] = useState(allowedHeights.SHORT);
  const cropperRef = useRef(null);

  const [infoOpen, setInfoOpen] = useState(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const inputRef = useRef(null);

  const isMulti = dialog.selection === "multi";
  const selectionLimit = isMulti ? dialog.selectionLimit || Infinity : 1;

  // Map mediaHeight prop → correct height
  useEffect(() => {
    if (dialog?.mediaHeight) {
      const height = allowedHeights[dialog.mediaHeight.toUpperCase()];
      if (height) {
        setTargetHeight(height);
      }
    }
  }, [dialog?.mediaHeight]);

  // Reset page to 1 on search or refresh
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, refreshKey]);

  // Update cropper aspect ratio when it changes
  useEffect(() => {
    if (showCropper && cropperRef.current?.cropper) {
      cropperRef.current.cropper.setAspectRatio(aspectRatio);
    }
  }, [aspectRatio, showCropper]);

  // Update cropper when target height changes
  useEffect(() => {
    if (showCropper && cropperRef.current?.cropper) {
      cropperRef.current.cropper.setAspectRatio(aspectRatio);
    }
  }, [targetHeight, showCropper, aspectRatio]);

  const url = useMemo(() => {
    if (dialog.isRcs) {
      return `/upload/rcs-media-list-1?sq=${debouncedSearchQuery}&type=${dialog.mediaType}&rk=${refreshKey}&page=${currentPage}`;
    }
    return `/upload/waba-media-list-1?sq=${debouncedSearchQuery}&type=${dialog.mediaType}&rk=${refreshKey}&page=${currentPage}`;
  }, [
    debouncedSearchQuery,
    dialog.mediaType,
    dialog.isRcs,
    refreshKey,
    currentPage,
  ]);

  const {
    data,
    isLoading,
    isError,
    error: apiError,
  } = useApiEp(url, !!url && dialog.isMediaOpen && !showCropper);

  // Load media from API
  useEffect(() => {
    if (dialog.isMediaOpen && !showCropper) {
      setError("");
      if (isLoading) return;
      if (isError) {
        setError(getErrorResponse(apiError));
        setMedia([]);
        setTotalPages(1);
      } else if (data) {
        const apiData = data.data || [];
        const mappedMedia = apiData.map((item) => ({
          id: item._id,
          fileName: item.file_name,
          previewUrl: item.preview_url,
          mediaId: item?.mediaId ?? "",
          mimeType: item.mime_type,
        }));
        setMedia(mappedMedia);
        setTotalPages(data.totalPages || 1);
        setAllMediaMap((prev) => {
          const updated = new Map(prev);
          mappedMedia.forEach((item) => updated.set(item.id, item));
          return updated;
        });
      }
    }
  }, [data, isLoading, isError, apiError, dialog.isMediaOpen, showCropper]);

  // Reset on close
  useEffect(() => {
    if (!dialog.isMediaOpen) {
      setSelectedMedia([]);
      setSearchQuery("");
      setError("");
      setRefreshKey(0);
      setCurrentPage(1);
      setTotalPages(1);
      setAllMediaMap(new Map());
      setShowCropper(false);
      setCropImage(null);
    }
  }, [dialog.isMediaOpen]);

  const getAcceptType = (type) => {
    if (type === "image") return "image/*";
    if (type === "video") return "video/*";
    if (type === "document") return "application/pdf";
    return "*/*";
  };

  const handleUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (dialog.isRcs && dialog.mediaType === "image") {
      // Show cropper instead of direct upload
      const reader = new FileReader();
      reader.onload = () => {
        setCropImage(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    } else {
      uploadFile(file);
    }
    e.target.value = "";
  };

  const uploadFile = async (file) => {
    setIsUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", dialog.mediaType);
      formData.append("mediaHeight", dialog.mediaHeight);

      const response = await fetch(`${API_ENDPOINT}/upload/rcs-media-upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!response.ok) throw new Error("Upload failed");

      setRefreshKey((prev) => prev + 1);
      setShowCropper(false);
      setCropImage(null);
    } catch (err) {
      setError("Upload failed: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropConfirm = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    // Get cropped canvas at full resolution - maintain aspect ratio only
    const canvas = cropper.getCroppedCanvas();
    if (!canvas) {
      setError("Unable to crop the image");
      return;
    }
    // High quality JPEG export
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const croppedFile = new File([blob], "cropped.jpg", {
            type: "image/jpeg",
          });
          uploadFile(croppedFile);
        }
      },
      "image/jpeg",
      0.95
    );
  };

  const handleSelect = (mediaId) => {
    setSelectedMedia((prev) => {
      const index = prev.indexOf(mediaId);
      if (index !== -1) return prev.filter((_, i) => i !== index);
      if (prev.length >= selectionLimit) {
        return isMulti ? prev : [mediaId];
      }
      return [...prev, mediaId];
    });
  };

  const handleOk = () => {
    const selectedMediaObjects = selectedMedia
      .map((id) => allMediaMap.get(id))
      .filter(Boolean);
    onMediaSelected?.(selectedMediaObjects);
    dialog.onConfirm?.(selectedMediaObjects);
    onClose();
  };

  const selectedCount = selectedMedia.length;
  const selectedInView = media.filter((item) =>
    selectedMedia.includes(item.id)
  ).length;
  const allSelected = media.length > 0 && selectedInView === media.length;
  const selectAllLabel = allSelected ? "Deselect all" : "Select all";

  const selectedHeightLabel = Object.entries(allowedHeights).find(
    ([k, v]) => v === targetHeight
  )?.[0];

  return (
    <Dialog
      open={dialog.isMediaOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, height: "80vh" } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography ml={1} variant="h6" fontWeight="600">
            Select Media
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 4 }}>
        {showCropper && cropImage ? (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="body2" color="primary">
                Using height: <b>{selectedHeightLabel}</b> ({targetHeight} DP)
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small" onClick={() => setInfoOpen(!infoOpen)}>
                  <InfoOutlined fontSize="small" />
                  <ExpandMore
                    fontSize="small"
                    sx={{
                      transform: infoOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.3s",
                    }}
                  />
                </IconButton>
              </Box>
            </Box>

            <Collapse in={infoOpen}>
              <Paper
                variant="outlined"
                sx={{ p: 1, mb: 2, bgcolor: "grey.50" }}
              >
                <Typography variant="caption" display="block">
                  <b>Crash course: When to use image sizes</b>
                </Typography>
                <Typography variant="caption" display="block">
                  • <b>SHORT (112 DP)</b>: Use for compact cards with minimal
                  media emphasis.
                </Typography>
                <Typography variant="caption" display="block">
                  • <b>MEDIUM (168 DP)</b>: Best for balanced layouts where text
                  and media both matter.
                </Typography>
                <Typography variant="caption" display="block">
                  • <b>TALL (264 DP)</b>: Use for visually rich cards where
                  image is primary focus.
                </Typography>
              </Paper>
            </Collapse>

            {/* Improved Controls Section */}
            <Box
              display="flex"
              gap={2}
              alignItems="center"
              mb={2}
              flexWrap="wrap"
            >
              <Box>
                <Typography variant="body2" gutterBottom>
                  Aspect Ratio
                </Typography>
                <select
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(Number(e.target.value))}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minWidth: "100px",
                  }}
                >
                  {aspectRatios.map((ar) => (
                    <option key={ar.label} value={ar.value}>
                      {ar.label}
                    </option>
                  ))}
                </select>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom>
                  Target Height
                </Typography>
                <select
                  value={targetHeight}
                  onChange={(e) => setTargetHeight(Number(e.target.value))}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minWidth: "100px",
                  }}
                >
                  {Object.entries(allowedHeights).map(([key, value]) => (
                    <option key={key} value={value}>
                      {key} ({value} DP)
                    </option>
                  ))}
                </select>
              </Box>
            </Box>

            <Cropper
              src={cropImage}
              style={{ height: 400, width: "100%" }}
              aspectRatio={aspectRatio}
              guides={true}
              viewMode={1}
              ref={cropperRef}
              responsive={true}
              restore={false}
              checkCrossOrigin={false}
            />

            <Box mt={2} display="flex" gap={2}>
              <Button variant="outlined" onClick={() => setShowCropper(false)}>
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                loading={isUploading}
                onClick={handleCropConfirm}
              >
                Crop & Upload
              </LoadingButton>
            </Box>
          </>
        ) : (
          // Media selection view
          <>
            {/* Search + Upload */}
            <Paper elevation={0}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search media..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <Search sx={{ color: "text.secondary", mr: 1 }} />
                    ),
                  }}
                />
                {dialog.isRcs && (
                  <LoadingButton
                    startIcon={<CloudUpload />}
                    onClick={handleUploadClick}
                    loading={isUploading}
                    variant="outlined"
                  >
                    Upload
                  </LoadingButton>
                )}
              </Box>
            </Paper>

            <input
              type="file"
              accept={getAcceptType(dialog.mediaType)}
              ref={inputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            )}

            {/* Media List */}
            <Box sx={{ overflow: "auto", mt: 1, height: "calc(100% - 24px)" }}>
              {isLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={200}
                >
                  <CircularProgress />
                </Box>
              ) : media.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Typography color="text.secondary">
                    {searchQuery
                      ? "No media found matching your search"
                      : "No media available"}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Grid container spacing={2}>
                    {media.map((item) => (
                      <Grid item xs={6} sm={4} md={3} key={item.id}>
                        <Card
                          sx={{
                            position: "relative",
                            border: selectedMedia.includes(item.id) ? 2 : 1,
                            borderColor: selectedMedia.includes(item.id)
                              ? "primary.main"
                              : "divider",
                          }}
                        >
                          <CardActionArea onClick={() => handleSelect(item.id)}>
                            {item.mimeType.startsWith("video/") ? (
                              <CardMedia
                                component="video"
                                height="140"
                                src={item.previewUrl}
                                autoPlay
                                muted
                                loop
                                playsInline
                                sx={{ objectFit: "cover" }}
                              />
                            ) : item.mimeType === "application/pdf" ? (
                              <Box
                                height="140px"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                sx={{ bgcolor: "grey.200", p: 1 }}
                              >
                                <PictureAsPdfIcon
                                  sx={{ fontSize: 48, color: "error.main" }}
                                />
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{
                                    mt: 1,
                                    maxWidth: "100%",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.fileName}
                                </Typography>
                              </Box>
                            ) : (
                              <CardMedia
                                component="img"
                                height="140"
                                image={item.previewUrl}
                                alt={item.fileName}
                                sx={{ objectFit: "cover" }}
                              />
                            )}
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(event, value) => setCurrentPage(value)}
                        size="small"
                        color="primary"
                        disabled={isLoading}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      {!showCropper && (
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleOk}
            variant="contained"
            disabled={
              dialog.selection === "multi"
                ? selectedCount !== dialog.selectionLimit
                : selectedCount === 0
            }
          >
            Select ({`${selectedCount}/${dialog.selectionLimit}`})
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default MediaSelectionModal;
